import {
  Flex,
  IconButton,
  IconEditLine,
  IconTrashLine,
  Pill,
  Text,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import RichTextEditor from "react-rte";
import { Card } from "../../../../../components/Card";
import {
  ERROR,
  useNotification,
} from "../../../../../components/CustomHooks/NotificationProvider";
import { EMPTY_RTE } from "../../../../../components/CustomTextArea/CustomRTE";
import { DialogYesNo } from "../../../../../components/DialogYesNo/DialogYesNo";
import ReflectionModal from "../../../../../components/Reflection/ReflectionModal";
import { ReflectionVersionsToggle } from "../../../../../components/Reflection/ReflectionVersionsToggle";
import {
  upsertReflection,
  upsertReflectionDraft,
  upsertReflectionDraftFromTemplate,
  upsertReflectionFromTemplate,
} from "../../../../../components/Reflection/utils";
import { ReflectionService } from "../../../../../services/ReflectionService";
import { UserDetailContext } from "../../../../../utils/Context";
import { formatToReadableTime } from "../../../../../utils/utils";

const ReflectionCard = ({
  reflection,
  sectionId,
  sectionName,
  sectionRefTemplate,
  setReflections,
}) => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const [openEdit, setOpenEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function handleRemoveReflection() {
    try {
      if (reflection.from_template) {
        const promiseList = reflection.sections.map((section) =>
          ReflectionService.deleteReflection({
            contextId: sectionId,
            reflectionId: reflection.ddl_id,
            sectionId: section.id,
            userId: reflection.is_draft ? userDetails.canvas_id : "",
          })
        );
        await Promise.all(promiseList);
      } else {
        await ReflectionService.deleteReflection({
          contextId: sectionId,
          reflectionId: reflection.ddl_id,
          userId: reflection.is_draft ? userDetails.canvas_id : "",
        });
      }

      setReflections((prevList) =>
        prevList.filter((item) => item.ddl_id !== reflection.ddl_id)
      );

      setConfirmDelete(false);
      showNotification(
        t("notifications.general.deleteSuccess", {
          obj: t("reflections.reflection"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.deleteError", {
          obj: t("reflections.reflection"),
        }),
        ERROR
      );
    }
  }

  async function handleUpdateReflection(reflectionText) {
    try {
      const updatedReflection = await upsertReflection({
        reflectionText,
        author: userDetails.name,
        contextId: sectionId,
        reflectionId: reflection.ddl_id,
        authorId: reflection.is_draft ? userDetails.canvas_id : null,
        actions: reflection.actions,
      });
      setReflections((prevList) => [
        updatedReflection,
        ...prevList.filter((item) => item.ddl_id !== reflection.ddl_id),
      ]);
      setOpenEdit(false);
      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("reflections.reflection"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("reflections.reflection"),
        }),
        ERROR
      );
    }
  }

  async function handleUpdateReflectionFromTemplate(templateId, sections) {
    try {
      const updatedReflection = await upsertReflectionFromTemplate({
        sections,
        author: userDetails.name,
        contextId: sectionId,
        templateId: templateId,
        reflectionId: reflection.ddl_id,
        authorId: reflection.is_draft ? userDetails.canvas_id : null,
        actions: reflection.actions,
      });
      setReflections((prevList) => [
        updatedReflection,
        ...prevList.filter((item) => item.ddl_id !== reflection.ddl_id),
      ]);
      setOpenEdit(false);
      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("reflections.reflection"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("reflections.reflection"),
        }),
        ERROR
      );
    }
  }

  async function handleSaveReflectionDraft(reflectionText, existingReflection) {
    try {
      const updatedReflection = await upsertReflectionDraft({
        reflectionText,
        author: userDetails.name,
        authorId: userDetails.canvas_id,
        contextId: sectionId,
        reflectionId: existingReflection.ddl_id,
      });
      setReflections((prevList) => [
        updatedReflection,
        ...prevList.filter((item) => item.ddl_id !== existingReflection.ddl_id),
      ]);
      setOpenEdit(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflectionDraft"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflectionDraft"),
        }),
        ERROR
      );
    }
  }

  async function handleSaveReflectionDraftFromTemplate(templateId, sections) {
    try {
      const updatedReflection = await upsertReflectionDraftFromTemplate({
        sections,
        author: userDetails.name,
        authorId: userDetails.canvas_id,
        contextId: sectionId,
        templateId: templateId,
        reflectionId: reflection.ddl_id,
      });
      setReflections((prevList) => [
        updatedReflection,
        ...prevList.filter((item) => item.ddl_id !== reflection.ddl_id),
      ]);
      setOpenEdit(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflectionDraft"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflectionDraft"),
        }),
        ERROR
      );
    }
  }

  return (
    <Card style={{ border: "1px solid #ccc", padding: "10px" }}>
      <Flex gap="small" wrap="wrap" alignItems="start">
        <Flex.Item shouldGrow>
          <Text as="p" size="small">
            {reflection.is_draft
              ? userDetails.name
              : reflection.metadata.author}
            <br />
            {formatToReadableTime(new Date(reflection.timestamp))}
          </Text>
        </Flex.Item>
        {reflection.is_draft && (
          <Flex.Item>
            <Pill color="success">{t("global.draft")}</Pill>
          </Flex.Item>
        )}
        <Flex.Item>
          <IconButton
            screenReaderLabel={t("reflections.editRef")}
            renderIcon={IconEditLine}
            size="small"
            onClick={() => setOpenEdit(true)}
          />
          <ReflectionModal
            openModal={openEdit}
            setOpenModal={setOpenEdit}
            reflection={reflection}
            handleSubmit={handleUpdateReflection}
            handleSubmitFromTemplate={handleUpdateReflectionFromTemplate}
            onDraftChange={handleSaveReflectionDraft}
            onDraftChangeFromTemplate={handleSaveReflectionDraftFromTemplate}
            contextName={sectionName}
            draftEnabled
            sectionRefTemplate={sectionRefTemplate}
          />
        </Flex.Item>
        <Flex.Item>
          <IconButton
            screenReaderLabel={t("reflections.deleteRef")}
            renderIcon={IconTrashLine}
            size="small"
            onClick={() => setConfirmDelete(true)}
          />
          <DialogYesNo
            message={t("reflections.deleteConfirm")}
            openStatus={confirmDelete}
            onClickYes={handleRemoveReflection}
            onClickNo={() => setConfirmDelete(false)}
          />
        </Flex.Item>

        {reflection.from_template ? (
          <Flex.Item size="100%">
            <Flex wrap="wrap" gap="medium">
              {reflection.sections.map((section) => (
                <Flex.Item key={section.id} width="100%">
                  <Flex wrap="wrap" gap="small">
                    <Flex.Item width="100%">
                      <Text weight="bold">{section.header_question}</Text>
                    </Flex.Item>
                    {section.helper_text &&
                      section.helper_text !== EMPTY_RTE && (
                        <Flex.Item>
                          <RichTextEditor
                            className="readOnlyClass"
                            value={RichTextEditor.createValueFromString(
                              section.helper_text,
                              "html"
                            )}
                            readOnly
                          />
                        </Flex.Item>
                      )}
                    <Flex.Item padding="0 0 0 large">
                      <div
                        style={{
                          borderLeft: "2px solid #03893D",
                          paddingLeft: "0.75rem",
                        }}
                      >
                        <RichTextEditor
                          className="readOnlyClass"
                          value={RichTextEditor.createValueFromString(
                            section.content,
                            "html"
                          )}
                          readOnly
                        />
                      </div>
                    </Flex.Item>
                  </Flex>
                </Flex.Item>
              ))}
            </Flex>
          </Flex.Item>
        ) : (
          <Flex.Item size="100%">
            <RichTextEditor
              className="readOnlyClass"
              value={RichTextEditor.createValueFromString(
                reflection.content,
                "html"
              )}
              readOnly
            />
          </Flex.Item>
        )}
        <Flex.Item size="100%">
          <ReflectionVersionsToggle
            contextId={sectionId}
            reflection={reflection}
            currentReflectionIsDraft={reflection.is_draft}
          />
        </Flex.Item>
      </Flex>
    </Card>
  );
};

export default ReflectionCard;
